import React from 'react';
import { CategoriesPage, CartDetail, CategoryProductsPage, ProductDetailPage } from '../index';
import { checkRequiredCookies, getCookie, setCookie, isBrowser, hasErrorProperties, scrollTo, getLocationHash } from '../../utils';
import { useFetchProducts, useHashChangeObserver } from '../../hooks';
import { useCategory, useNavigation, useOrderManagement } from '../../context';
import {
    COOKIE_CLOSE_WEBVIEW_TO_PREVIOUS_URL,
    PAGE_CART_DETAIL,
    PAGE_CATEGORY,
    PAGE_PRODUCT_DETAIL,
    PAGE_ITEM_LIST,
    requiredCookies,
    ERROR_MESSAGE_COOKIES,
    ERROR_MESSAGE_CATEGORY_ERROR,
    ERROR_MESSAGE_ORDER_ERROR
} from '../../components/constants';
import { OrderBar, BackNavigation } from '../../components';
import { ErrorPage } from '../error-page';
import { defaultFontFamily } from '../../styles';

export const RouteSwitcher = () => {
    const { categories, filteredCategories, mainCategoryId, error: categoryError, isCategoryLoading } = useCategory();
    const { orderError, isOrderLoading, setProduct } = useOrderManagement();
    const { navigateToItem, navigateToCategoryProductsPage, navigateToCart, navigateToCategoryPage } = useNavigation();
    const { products } = useFetchProducts(mainCategoryId);

    const closeWebViewToPreviousUrl = getCookie(COOKIE_CLOSE_WEBVIEW_TO_PREVIOUS_URL);

    useHashChangeObserver();
    const withTemplate = (content: JSX.Element): JSX.Element => <div style={{ ...defaultFontFamily }}>{content}</div>;

    const renderPage = () => {
        scrollTo();
        const currentHash = getLocationHash().split('/');
        switch (currentHash[0]) {
            case PAGE_CATEGORY:
            case '':
                const mainCategory = categories.find(({ categoryId }) => categoryId === mainCategoryId);
                setCookie('subCategory', '');
                return (
                    <>
                        <OrderBar navigateToCart={navigateToCart} />
                        <CategoriesPage
                            categories={filteredCategories}
                            categoryId={mainCategoryId ?? undefined}
                            navigateToCategoryProductsPage={navigateToCategoryProductsPage}
                            navigateToCategoryPage={navigateToCategoryPage}
                            cookieCategory={mainCategory}
                        />
                    </>
                );
            case PAGE_ITEM_LIST:
                const categoryId = parseInt(currentHash[1], 10);
                return (
                    <>
                        <BackNavigation />
                        <OrderBar navigateToCart={navigateToCart} />
                        <CategoryProductsPage
                            categoryName={categories.find(c => c.categoryId === categoryId)?.name ?? '-'}
                            categoryDescription={categories.find(c => c.categoryId === categoryId)?.localizedDescription ?? ''}
                            categoryId={categoryId}
                            navigateToItem={navigateToItem}
                        />
                    </>
                );
            case PAGE_PRODUCT_DETAIL:
                const product = products ? products.find(({ productId }) => productId === currentHash[1]) : undefined;
                setProduct(product);
                return (
                    <>
                        <BackNavigation />
                        <ProductDetailPage navigateToCategoryProductsPage={navigateToCategoryProductsPage} />
                    </>
                );
            case PAGE_CART_DETAIL:
                return (
                    <>
                        <BackNavigation />
                        <CartDetail />
                    </>
                );
            default:
                return <ErrorPage message={ERROR_MESSAGE_CATEGORY_ERROR} />;
        }
    };

    try {
        checkRequiredCookies(requiredCookies);
    } catch (error) {
        return <ErrorPage message={ERROR_MESSAGE_COOKIES} />;
    }

    if (isOrderLoading || isCategoryLoading) {
        return (
            <div className='over-spinner'>
                <img title='loader' className='spinner' />
            </div>
        );
    }

    if (orderError && hasErrorProperties(orderError) && orderError.userAuthenticationError) {
        return (
            <ErrorPage message={orderError.userAuthenticationError}>
                <button
                    onClick={() => {
                        if (closeWebViewToPreviousUrl) {
                            window.location.href = closeWebViewToPreviousUrl;
                        }
                    }}
                    style={{
                        fontSize: '16px',
                        background: 'black',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '10px 24px',
                        textTransform: 'uppercase',
                        borderRadius: '20px 0 20px 0',
                        marginTop: '16px',
                        textDecoration: 'none',
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    Close Menu
                </button>
            </ErrorPage>
        );
    }

    if (orderError && hasErrorProperties(orderError) && (orderError.orderFetchError || orderError.orderUpdateError) && isBrowser()) {
        return (
            <ErrorPage message={ERROR_MESSAGE_ORDER_ERROR}>
                {orderError.orderFetchError && <p>{orderError.orderFetchError}</p>}
                {orderError.orderUpdateError && <p>{orderError.orderUpdateError}</p>}
            </ErrorPage>
        );
    }

    if (categoryError) {
        return <ErrorPage message={ERROR_MESSAGE_CATEGORY_ERROR} />;
    }

    return withTemplate(renderPage());
};
